import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { AnimatedSpinner } from '../../../components/AnimatedSpinner';
import { CheckCircle } from '../../../components/Icons';
import { IParsedFeatures } from '../../../types/subscription';

import { extraQueryParams } from '@utils/constants';
import { formatNumber } from '@utils/numbersFormatting';
import { analytics } from '../../../utils/analytics';
import './styles.css';

const napsterLogo = '/assets/images/naspter-view-logo.png';

interface PricingCardProps {
  onCheckout?: (
    price_id: PricingCardProps['priceId'],
    returnURL: string,
    cancelUrl: string,
  ) => void;
  isAuthenticated: boolean;
  link?: string;
  priceId: string;
  name: string;
  price: number;
  description: string;
  features?: string;
  per?: string;
  hours?: string;
  subsPlanName?: string;
  freeSubsName?: string;
  isLoading?: boolean;
  isHardware?: boolean;
  subPriceId?: string;
  units?: number;
  isCurrentUserAdmin?: boolean;
}

export function PricingCard({
  priceId,
  name,
  price,
  description,
  features,
  onCheckout,
  per,
  isAuthenticated,
  link,
  subsPlanName,
  freeSubsName,
  isLoading,
  isHardware,
  subPriceId,
  units,
  isCurrentUserAdmin = true,
}: PricingCardProps) {
  const { t } = useTranslation();
  const dollarsAndCents = (price / 100).toFixed(2).split('.');

  const isActive = useMemo(() => {
    return subsPlanName && isAuthenticated && priceId === subPriceId;
  }, [isAuthenticated, subsPlanName, priceId, subPriceId]);

  const [isProgressing, setProgressing] = useState(false);

  const auth = useAuth();

  const parsedFeatures = useMemo<IParsedFeatures>(
    () => JSON.parse(features ?? '[]') as IParsedFeatures,
    [features],
  );

  const handleClick = () => {
    // Track napster_view_clicked event for hardware products
    if (isHardware) {
      analytics.logEvent('napster_view_clicked', {});
    }

    // Track subscription_clicked event
    const subscriptionDuration =
      per === 'month' ? 'monthly' : per === 'year' ? 'annual' : per;

    analytics.logEvent('subscription_clicked', {
      subscription_type: name,
      subscription_duration: subscriptionDuration || 'unknown',
    });

    setProgressing(true);
    if (auth.user) {
      const urlParams = new URLSearchParams(window.location.search);
      let nextUrl = urlParams.get('next_url');
      nextUrl =
        nextUrl === null || nextUrl === undefined || nextUrl === ''
          ? `${window.location.origin}/pricing`
          : nextUrl;
      onCheckout?.(priceId, nextUrl, name);
      return;
    }
    if (price === 0) {
      void auth.signinRedirect({
        extraQueryParams: { ...extraQueryParams, action: 'get-started' },
      });
      return;
    }

    const fullPath = `/pricing-checkout?priceId=${priceId}`;
    void auth.signinRedirect({
      state: {
        path: fullPath,
        returnUrl: '/pricing',
      },
      extraQueryParams: {
        ...extraQueryParams,
        action: 'get-started',
      },
    });
  };

  const isFreeCurrentPlan = useMemo(() => {
    return subsPlanName && isAuthenticated && priceId === subPriceId;
  }, [isAuthenticated, subsPlanName, priceId, subPriceId]);

  const isFreePlanWithPaidSubscription = useMemo(() => {
    return (
      isAuthenticated &&
      price === 0 &&
      subsPlanName &&
      subsPlanName !== freeSubsName
    );
  }, [isAuthenticated, price, subsPlanName, freeSubsName]);

  const renderButton = () => {
    if (isLoading) {
      return (
        <span className="pricing-card-button text-black bg-white">
          <AnimatedSpinner className="text-black bg-white" />
        </span>
      );
    }

    if (isFreePlanWithPaidSubscription) {
      return <span className="pricing-card-button text-black bg-white" />;
    }

    if (link) {
      if (isActive) {
        return (
          <span className="pricing-card-button text-black bg-white">
            {t('pricing.currentPlan')}
          </span>
        );
      }

      if (!isCurrentUserAdmin) {
        return (
          <span className="pricing-card-button text-black bg-white">
            {t('pricing.askOwner')}
          </span>
        );
      }

      const handleSubscribeClick = () => {
        // Track upgrade_button_clicked event when clicking Subscribe link
        const planType =
          per === 'month' ? 'Monthly' : per === 'year' ? 'Annual' : 'Other';
        const priceDisplayed = (price / 100).toFixed(2);

        analytics.logEvent('upgrade_button_clicked', {
          plan_type: planType,
          price_displayed: priceDisplayed,
        });
      };

      return (
        <a
          href={link}
          className="pricing-card-button text-black bg-white"
          target="_blank"
          rel="noreferrer noopener"
          onClick={handleSubscribeClick}>
          {t('pricing.subscribe')}
        </a>
      );
    }

    if (isFreeCurrentPlan) {
      return (
        <span className="pricing-card-button text-black bg-white">
          Current Plan
        </span>
      );
    }

    if (!isCurrentUserAdmin && isAuthenticated) {
      return (
        <span className="pricing-card-button text-black bg-white">
          Ask owner
        </span>
      );
    }

    return (
      <button
        className="pricing-card-button text-black bg-white"
        onClick={handleClick}>
        <span className="price-label p-2 text-black">
          {isAuthenticated ? t('pricing.subscribe') : t('pricing.getStarted')}
        </span>
        {isProgressing && <AnimatedSpinner className="text-black bg-white" />}
      </button>
    );
  };

  return (
    <div
      className={`pricing-card-container ${
        isHardware ? '!max-w-full !min-w-0' : ''
      }`}>
      <div
        className={`pricing-card py-6 px-6 ${isHardware ? 'pt-0 ' : ''}${
          (parsedFeatures?.isPopular && subsPlanName === freeSubsName) ||
          (parsedFeatures?.isPopular && !isAuthenticated) ||
          isActive
            ? 'tc-mv-blur-brown'
            : 'tc-mv-blur'
        }`}>
        {isHardware ? (
          <>
            <img
              className="w-full h-auto"
              src="/assets/images/banner-view.png"
            />
          </>
        ) : (
          <>
            <h3 className="pricing-card-name">
              <div className="flex items-center font-[NapsterSans]">
                <span className="font-['DM_Serif_Text']">{name}</span>
                {parsedFeatures?.isPopular && (
                  <div className="pricing-card-name-badge px-2 py-1 font-[NapsterSans] me-4 sm:text-base text-sm text-center">
                    {t('pricing.bestValue')}
                  </div>
                )}
              </div>
              <img
                src={napsterLogo}
                alt="Napster View"
                className={`pricing-card-logo ${
                  parsedFeatures?.hasView
                    ? 'sm:block hidden mb-[-20px]'
                    : 'hidden'
                }`}
              />
            </h3>
            <div className="pricing-card-price my-5 sm:my-2 mb-0 ">
              <span className="price">${dollarsAndCents[0]}</span>
              {!!per && <span className="period">/{per}</span>}
              <img
                src={napsterLogo}
                alt="Napster View"
                className={`pricing-card-logo ml-auto ${
                  parsedFeatures?.isPopular ? 'block sm:hidden' : 'hidden'
                }`}
              />
            </div>
            <div className="pricing-card-description">
              <p className="text-2xl font-[600]">
                {formatNumber(units || 0)} {t('pricing.credits')}
                {per && <span>{t('pricing.perMonth')}</span>}
              </p>
              <p className="text-sm">{description}</p>
            </div>

            <div className="flex min-h-[60px] flex-col sm:flex-row sm:mt-0 mt-8">
              <ul className="pricing-card-features">
                {parsedFeatures?.items?.map((feature, index) => (
                  <li key={index} className="flex flex-col gap-2">
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 shrink-0" />
                      {feature.html ? (
                        <span
                          className="text-sm text-white w-full"
                          dangerouslySetInnerHTML={{ __html: feature.html }}
                        />
                      ) : (
                        <span className="">{feature.text}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>

      {renderButton()}
    </div>
  );
}
